import React, { useState, useEffect } from 'react';
import { Icon } from '@iconify/react/dist/iconify.js';
import axios from 'axios';
import { Card, Button, Form, Alert, InputGroup, Modal, OverlayTrigger, Tooltip, Badge, Spinner } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';

// Debug Component
const DebugServices = ({ services, mitraId, onRefresh }) => {
    const [showDebug, setShowDebug] = useState(false);
    
    if (!showDebug) {
        return (
            <Button 
                variant="outline-info" 
                size="sm" 
                onClick={() => setShowDebug(true)}
                className="mb-3"
            >
                Show Debug Info
            </Button>
        );
    }

    return (
        <Card className="mb-3">
            <Card.Header className="d-flex justify-content-between align-items-center">
                <h6 className="mb-0">Debug Information</h6>
                <div>
                    <Button 
                        variant="outline-primary" 
                        size="sm" 
                        onClick={onRefresh}
                        className="me-2"
                    >
                        Refresh Data
                    </Button>
                    <Button 
                        variant="outline-secondary" 
                        size="sm" 
                        onClick={() => setShowDebug(false)}
                    >
                        Hide
                    </Button>
                </div>
            </Card.Header>
            <Card.Body>
                <Alert variant="info">
                    <strong>Mitra ID:</strong> {mitraId}<br/>
                    <strong>Total Services:</strong> {services.length}<br/>
                    <strong>Active Services:</strong> {services.filter(s => s.is_active).length}<br/>
                    <strong>Inactive Services:</strong> {services.filter(s => !s.is_active).length}
                </Alert>
                
                <div className="table-responsive">
                    <table className="table table-sm">
                        <thead>
                            <tr>
                                <th>ID</th>
                                <th>Nama Layanan</th>
                                <th>Status</th>
                                <th>Kategori</th>
                                <th>Harga</th>
                            </tr>
                        </thead>
                        <tbody>
                            {services.map(service => (
                                <tr key={service.id}>
                                    <td>{service.id}</td>
                                    <td>{service.nama_layanan}</td>
                                    <td>
                                        <Badge bg={service.is_active ? 'success' : 'secondary'}>
                                            {service.is_active ? 'Aktif' : 'Nonaktif'}
                                        </Badge>
                                    </td>
                                    <td>{service.kategori}</td>
                                    <td>Rp {service.harga?.toLocaleString('id-ID') || 'N/A'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                
                <details className="mt-3">
                    <summary>Raw JSON Data</summary>
                    <pre className="bg-light p-2 mt-2" style={{ fontSize: '12px', maxHeight: '300px', overflow: 'auto' }}>
                        {JSON.stringify(services, null, 2)}
                    </pre>
                </details>
            </Card.Body>
        </Card>
    );
};

const MitraServices = ({ mitraId }) => {
    const [services, setServices] = useState([]);
    const [filteredServices, setFilteredServices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [categories, setCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [showEditModal, setShowEditModal] = useState(false);
    const [currentService, setCurrentService] = useState(null);
    const [customPrice, setCustomPrice] = useState('');
    const [processingServices, setProcessingServices] = useState([]);

    // Base URL for API
    const API_BASE_URL = 'https://api.tomassage.id/api';

    // Fetch services for the mitra
    const fetchServices = async () => {
        try {
            setLoading(true);
            setError(null); // Clear previous errors
            
            console.log('Fetching services for mitra:', mitraId);
            
            const response = await axios.get(`${API_BASE_URL}/${mitraId}/services`);
            
            console.log('Fetch services response:', response.data);
            
            if (response.data.success) {
                const servicesData = response.data.data;
                
                console.log('Services data received:', servicesData);
                console.log('Number of services:', servicesData.length);
                
                // Log each service status
                servicesData.forEach(service => {
                    console.log(`Service: ${service.nama_layanan}, Active: ${service.is_active}, ID: ${service.id}`);
                });
                
                // Extract unique categories
                const uniqueCategories = [...new Set(servicesData.map(service => service.kategori))];
                setCategories(uniqueCategories);
                
                setServices(servicesData);
                setFilteredServices(servicesData);
            } else {
                console.error('Fetch services failed:', response.data);
                setError(response.data.message || 'Gagal memuat data layanan');
            }
        } catch (err) {
            console.error('Error fetching services:', err);
            console.error('Error details:', {
                message: err.message,
                response: err.response?.data,
                status: err.response?.status
            });
            setError(err.response?.data?.message || 'Gagal memuat daftar layanan');
        } finally {
            setLoading(false);
        }
    };

    // Toggle service status
    const toggleServiceStatus = async (serviceId, currentStatus) => {
        try {
            // Add to processing list immediately
            setProcessingServices(prev => [...prev, serviceId]);
            setError(null);
            
            // Find the service being updated for logging
            const serviceBeingUpdated = services.find(s => s.id === serviceId);
            console.log('Toggling service:', {
                serviceId,
                serviceName: serviceBeingUpdated?.nama_layanan,
                currentStatus,
                newStatus: !currentStatus,
                mitraId
            });
            
            const requestData = {
                is_active: !currentStatus
            };
            
            console.log('Request URL:', `${API_BASE_URL}/${mitraId}/services/${serviceId}`);
            console.log('Request data:', requestData);
            
            const response = await axios.put(`${API_BASE_URL}/${mitraId}/services/${serviceId}`, requestData);
            
            console.log('API Response:', response.data);
            
            if (response.data.success) {
                // Update local state immediately
                const updatedServices = services.map(service => 
                    service.id === serviceId 
                        ? { ...service, is_active: !currentStatus } 
                        : service
                );
                
                console.log('Updated service in local state:', {
                    serviceId,
                    oldStatus: currentStatus,
                    newStatus: !currentStatus
                });
                
                setServices(updatedServices);
                
                // Apply current filters to updated data
                applyFilters(updatedServices, searchTerm, selectedCategory);
                
                setSuccessMessage(`Layanan "${serviceBeingUpdated?.nama_layanan}" berhasil ${!currentStatus ? 'diaktifkan' : 'dinonaktifkan'}`);
                
                // Clear success message after 3 seconds
                setTimeout(() => {
                    setSuccessMessage(null);
                }, 3000);
                
                // Refetch data after a short delay to ensure server state is in sync
                setTimeout(() => {
                    fetchServices();
                }, 1500);
            } else {
                console.error('API returned success=false:', response.data);
                setError(response.data.message || 'Gagal mengubah status layanan');
                
                // Refetch data to ensure consistency
                setTimeout(() => {
                    fetchServices();
                }, 1000);
            }
        } catch (err) {
            console.error('Error toggling service status:', err);
            console.error('Error details:', {
                message: err.message,
                response: err.response?.data,
                status: err.response?.status
            });
            
            setError(err.response?.data?.message || `Gagal mengubah status layanan: ${err.message}`);
            
            // If error occurs, refetch data to ensure consistency
            setTimeout(() => {
                fetchServices();
            }, 1000);
        } finally {
            // Always remove from processing list
            setProcessingServices(prev => prev.filter(id => id !== serviceId));
        }
    };

    // Update service price
    const updateServicePrice = async () => {
        if (!currentService) return;
        
        try {
            setProcessingServices(prev => [...prev, currentService.id]);
            setError(null);
            
            const price = parseInt(customPrice);
            if (isNaN(price) || price <= 0) {
                setError('Harga harus berupa angka positif');
                setProcessingServices(prev => prev.filter(id => id !== currentService.id));
                return;
            }
            
            const response = await axios.put(`${API_BASE_URL}/${mitraId}/services/${currentService.id}`, {
                harga_kustom: price
            });
            
            if (response.data.success) {
                // Update local state
                const updatedServices = services.map(service => 
                    service.id === currentService.id 
                        ? { ...service, harga: price } 
                        : service
                );
                
                setServices(updatedServices);
                applyFilters(updatedServices, searchTerm, selectedCategory);
                
                setSuccessMessage('Harga layanan berhasil diperbarui');
                setShowEditModal(false);
                setCurrentService(null);
                setCustomPrice('');
                
                // Hide success message after 3 seconds
                setTimeout(() => {
                    setSuccessMessage(null);
                }, 3000);
            } else {
                setError(response.data.message || 'Gagal memperbarui harga layanan');
            }
        } catch (err) {
            console.error('Error updating service price:', err);
            setError(err.response?.data?.message || 'Gagal memperbarui harga layanan');
        } finally {
            setProcessingServices(prev => prev.filter(id => id !== currentService.id));
        }
    };

    // Apply filters (search and category)
    const applyFilters = (data, search, category) => {
        let filtered = [...data]; // Create a copy to avoid mutations
        
        // Apply category filter
        if (category && category !== 'all') {
            filtered = filtered.filter(service => service.kategori === category);
        }
        
        // Apply search filter
        if (search) {
            const searchLower = search.toLowerCase();
            filtered = filtered.filter(service => 
                service.nama_layanan.toLowerCase().includes(searchLower) || 
                service.deskripsi.toLowerCase().includes(searchLower) ||
                service.kategori.toLowerCase().includes(searchLower)
            );
        }
        
        setFilteredServices(filtered);
    };

    // Handle search
    const handleSearch = (e) => {
        const value = e.target.value;
        setSearchTerm(value);
        applyFilters(services, value, selectedCategory);
    };

    // Handle category filter change
    const handleCategoryChange = (category) => {
        setSelectedCategory(category);
        applyFilters(services, searchTerm, category);
    };

    // Reset search and filters
    const resetFilters = () => {
        setSearchTerm('');
        setSelectedCategory('all');
        setFilteredServices(services);
    };

    // Handle modal close
    const handleCloseEditModal = () => {
        setShowEditModal(false);
        setCurrentService(null);
        setCustomPrice('');
        setError(null);
    };

    // Initial fetch
    useEffect(() => {
        if (mitraId) {
            fetchServices();
        }
    }, [mitraId]);

    // Re-apply filters when services change
    useEffect(() => {
        if (services.length > 0) {
            applyFilters(services, searchTerm, selectedCategory);
        }
    }, [services]);

    // Auto-hide messages
    useEffect(() => {
        if (successMessage) {
            const timer = setTimeout(() => {
                setSuccessMessage(null);
            }, 3000);
            return () => clearTimeout(timer);
        }
    }, [successMessage]);

    useEffect(() => {
        if (error) {
            const timer = setTimeout(() => {
                setError(null);
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [error]);

    // Loading state
    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
                <Spinner animation="border" variant="primary" />
                <span className="ms-2">Memuat layanan...</span>
            </div>
        );
    }

    return (
        <div className="services-management">
            {/* Debug Component */}
            <DebugServices 
                services={services} 
                mitraId={mitraId} 
                onRefresh={fetchServices}
            />

            {/* Success Message */}
            {successMessage && (
                <Alert 
                    variant="success" 
                    onClose={() => setSuccessMessage(null)} 
                    dismissible
                    className="mb-3"
                >
                    <Icon icon="mdi:check-circle" className="me-2" />
                    {successMessage}
                </Alert>
            )}

            {/* Error Message */}
            {error && (
                <Alert 
                    variant="danger" 
                    onClose={() => setError(null)} 
                    dismissible
                    className="mb-3"
                >
                    <Icon icon="mdi:alert-circle" className="me-2" />
                    {error}
                </Alert>
            )}

            {/* Filters Section */}
            <Card className="mb-4 shadow-sm">
                <Card.Header className="bg-white">
                    <h5 className="mb-0">Filter Layanan</h5>
                </Card.Header>
                <Card.Body>
                    <div className="row">
                        <div className="col-md-6 mb-3">
                            <Form.Group>
                                <Form.Label>Pencarian</Form.Label>
                                <InputGroup>
                                    <Form.Control
                                        placeholder="Cari nama atau deskripsi layanan..."
                                        value={searchTerm}
                                        onChange={handleSearch}
                                    />
                                    {searchTerm && (
                                        <Button 
                                            variant="outline-secondary" 
                                            onClick={() => {
                                                setSearchTerm('');
                                                applyFilters(services, '', selectedCategory);
                                            }}
                                        >
                                            <Icon icon="mdi:close" />
                                        </Button>
                                    )}
                                </InputGroup>
                            </Form.Group>
                        </div>
                        <div className="col-md-6 mb-3">
                            <Form.Group>
                                <Form.Label>Kategori</Form.Label>
                                <Form.Select 
                                    value={selectedCategory}
                                    onChange={(e) => handleCategoryChange(e.target.value)}
                                >
                                    <option value="all">Semua Kategori</option>
                                    {categories.map((category, index) => (
                                        <option key={index} value={category}>
                                            {category}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </div>
                    </div>
                    <div className="d-flex justify-content-between align-items-center mt-2">
                        <span className="text-muted">
                            Menampilkan {filteredServices.length} dari {services.length} layanan
                        </span>
                        {(searchTerm || selectedCategory !== 'all') && (
                            <Button 
                                variant="outline-secondary" 
                                size="sm" 
                                onClick={resetFilters}
                            >
                                <Icon icon="mdi:filter-remove" className="me-1" />
                                Reset Filter
                            </Button>
                        )}
                    </div>
                </Card.Body>
            </Card>

            {/* Services List */}
            <div className="row">
                {filteredServices.length > 0 ? (
                    filteredServices.map(service => (
                        <div key={service.id} className="col-md-6 col-lg-4 mb-4">
                            <Card className="h-100 shadow-sm position-relative">
                                {/* Loading overlay for individual cards */}
                                {processingServices.includes(service.id) && (
                                    <div 
                                        className="position-absolute w-100 h-100 d-flex align-items-center justify-content-center"
                                        style={{
                                            backgroundColor: 'rgba(255, 255, 255, 0.8)',
                                            zIndex: 10,
                                            borderRadius: '0.375rem'
                                        }}
                                    >
                                        <Spinner animation="border" variant="primary" />
                                    </div>
                                )}
                                
                                <div className="position-relative">
                                    <div 
                                        className="card-img-top bg-light d-flex justify-content-center align-items-center" 
                                        style={{ height: '180px', overflow: 'hidden' }}
                                    >
                                        {service.icon_url ? (
                                            <img 
                                                src={service.icon_url} 
                                                alt={service.nama_layanan} 
                                                className="img-fluid" 
                                                style={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'cover' }}
                                                onError={(e) => {
                                                    e.target.onerror = null;
                                                    e.target.src = "https://via.placeholder.com/200x150?text=No+Image";
                                                }}
                                            />
                                        ) : (
                                            <div className="text-center text-muted">
                                                <Icon icon="mdi:image-off" size={48} />
                                                <p>Tidak ada gambar</p>
                                            </div>
                                        )}
                                    </div>
                                    <Badge 
                                        bg={service.is_active ? 'success' : 'secondary'} 
                                        className="position-absolute top-0 end-0 m-2"
                                    >
                                        {service.is_active ? 'Aktif' : 'Nonaktif'}
                                    </Badge>
                                </div>
                                <Card.Body className="d-flex flex-column">
                                    <div className="d-flex justify-content-between mb-2">
                                        <Badge bg="info">{service.kategori}</Badge>
                                        <small className="text-muted">{service.durasi_menit} menit</small>
                                    </div>
                                    <Card.Title>{service.nama_layanan}</Card.Title>
                                    <Card.Text className="text-muted small mb-3" style={{ maxHeight: '80px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        {service.deskripsi}
                                    </Card.Text>
                                    <div className="mt-auto">
                                        <div className="d-flex justify-content-between align-items-center mb-3">
                                            <h5 className="mb-0">Rp {service.harga?.toLocaleString('id-ID')}</h5>
                                            <OverlayTrigger
                                                placement="top"
                                                overlay={
                                                    <Tooltip>
                                                        Edit harga
                                                    </Tooltip>
                                                }
                                            >
                                                <Button 
                                                    variant="outline-primary" 
                                                    size="sm"
                                                    onClick={() => {
                                                        setCurrentService(service);
                                                        setCustomPrice(service.harga?.toString() || '');
                                                        setShowEditModal(true);
                                                    }}
                                                    disabled={processingServices.includes(service.id)}
                                                >
                                                    <Icon icon="mdi:pencil" />
                                                </Button>
                                            </OverlayTrigger>
                                        </div>
                                        <div className="d-grid">
                                            <Button
                                                variant={service.is_active ? "danger" : "success"}
                                                onClick={() => {
                                                    console.log('Button clicked for service:', {
                                                        id: service.id,
                                                        name: service.nama_layanan,
                                                        currentStatus: service.is_active,
                                                        willBecome: !service.is_active
                                                    });
                                                    toggleServiceStatus(service.id, service.is_active);
                                                }}
                                                disabled={processingServices.includes(service.id)}
                                            >
                                                {processingServices.includes(service.id) ? (
                                                    <>
                                                        <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-1" />
                                                        Processing...
                                                    </>
                                                ) : (
                                                    <>
                                                        <Icon 
                                                            icon={service.is_active ? "mdi:close-circle" : "mdi:check-circle"} 
                                                            className="me-1" 
                                                        />
                                                        {service.is_active ? "Nonaktifkan" : "Aktifkan"}
                                                    </>
                                                )}
                                            </Button>
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>
                        </div>
                    ))
                ) : (
                    <div className="col-12 text-center py-5">
                        <Icon icon="mdi:package-variant-closed-remove" size={64} className="text-muted mb-3" />
                        <h5>Tidak ada layanan ditemukan</h5>
                        <p className="text-muted">
                            {searchTerm || selectedCategory !== 'all' 
                                ? 'Coba ubah filter pencarian Anda'
                                : 'Tidak ada layanan yang tersedia untuk mitra ini'}
                        </p>
                        {(searchTerm || selectedCategory !== 'all') && (
                            <Button 
                                variant="outline-primary" 
                                onClick={resetFilters}
                            >
                                Reset Filter
                            </Button>
                        )}
                    </div>
                )}
            </div>

            {/* Edit Price Modal */}
            <Modal show={showEditModal} onHide={handleCloseEditModal}>
                <Modal.Header closeButton>
                    <Modal.Title>Edit Harga Layanan</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {currentService && (
                        <>
                            <h5>{currentService.nama_layanan}</h5>
                            <p className="text-muted small mb-3">{currentService.deskripsi}</p>
                            
                            <Form.Group className="mb-3">
                                <Form.Label>Harga Layanan (Rp)</Form.Label>
                                <Form.Control
                                    type="number"
                                    value={customPrice}
                                    onChange={(e) => setCustomPrice(e.target.value)}
                                    placeholder="Masukkan harga kustom"
                                    min="0"
                                />
                                <Form.Text className="text-muted">
                                    Masukkan harga tanpa tanda titik atau koma.
                                </Form.Text>
                            </Form.Group>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseEditModal}>
                        Batal
                    </Button>
                    <Button 
                        variant="primary" 
                        onClick={updateServicePrice}
                        disabled={processingServices.includes(currentService?.id)}
                    >
                        {processingServices.includes(currentService?.id) ? (
                            <>
                                <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-1" />
                                Menyimpan...
                            </>
                        ) : (
                            <>
                                <Icon icon="mdi:content-save" className="me-1" />
                                Simpan Perubahan
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default MitraServices;